import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Download, CheckCircle2, Mail } from "lucide-react";
import api from "@/lib/api";

const DOC_META = {
  mecenat: { label: "Dossier de mécénat", eyebrow: "§ Téléchargement · Dossier stratégique" },
  statuts: { label: "Statuts · Les Arteurs",     eyebrow: "§ Téléchargement · Document officiel" },
  joafe:   { label: "Déclaration JOAFE",         eyebrow: "§ Téléchargement · Journal Officiel" },
};

/**
 * DownloadGateModal — 2-step gated document download.
 * Step 1: identification form + purpose. On submit, backend emails a 6-digit code.
 * Step 2: user enters the code. On success, download URL is revealed.
 */
export default function DownloadGateModal({ open, onClose, document = "mecenat" }) {
  const meta = DOC_META[document] || DOC_META.mecenat;
  // Rehydrate previous identity (email + token) so returning users skip the form
  const [form, setForm] = useState(() => {
    if (typeof window === "undefined") return { name: "", email: "", organization: "", role: "", phone: "", purpose: "" };
    try {
      const cached = JSON.parse(localStorage.getItem("fdm_identity") || "{}");
      return {
        name: cached.name || "",
        email: cached.email || "",
        organization: cached.organization || "",
        role: cached.role || "",
        phone: cached.phone || "",
        purpose: "",
      };
    } catch { return { name: "", email: "", organization: "", role: "", phone: "", purpose: "" }; }
  });
  const [identityToken, setIdentityToken] = useState(() => {
    if (typeof window === "undefined") return null;
    try { return JSON.parse(localStorage.getItem("fdm_identity") || "{}").token || null; }
    catch { return null; }
  });
  const [stage, setStage] = useState("form"); // form | code | done
  const [requestId, setRequestId] = useState(null);
  const [code, setCode] = useState("");
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submitForm = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErrMsg("");
    try {
      const { data } = await api.post("/downloads/request", { document, ...form, identity_token: identityToken });
      if (data.verified && data.url) {
        // Fast-tracked path — user was already identified
        setDownloadUrl(data.url);
        setStage("done");
      } else {
        setRequestId(data.request_id);
        setStage("code");
      }
    } catch (err) {
      setErrMsg(err?.response?.data?.detail || "Une erreur est survenue. Réessayez.");
    } finally {
      setSubmitting(false);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErrMsg("");
    try {
      const { data } = await api.post("/downloads/verify", { request_id: requestId, code: code.replace(/\s/g, "") });
      setDownloadUrl(data.url);
      // Persist identity so subsequent downloads skip the flow
      try {
        localStorage.setItem("fdm_identity", JSON.stringify({
          token: data.identity_token || requestId,
          name: form.name, email: form.email, organization: form.organization, role: form.role, phone: form.phone,
        }));
        setIdentityToken(data.identity_token || requestId);
      } catch {}
      setStage("done");
    } catch (err) {
      setErrMsg(err?.response?.data?.detail || "Code incorrect ou expiré.");
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    setStage("form");
    setRequestId(null);
    setCode("");
    setDownloadUrl(null);
    setErrMsg("");
    setSubmitting(false);
    // Do NOT clear form/identity — user may reopen the modal for another document
    onClose?.();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-fg/70 backdrop-blur-md flex items-center justify-center p-4 md:p-8 overflow-y-auto"
          onClick={close}
          data-testid="download-gate-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3 }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-bg border border-fg/15 w-full max-w-2xl my-8"
          >
            <button
              type="button"
              onClick={close}
              className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center hover:bg-fg/5 transition-colors z-10"
              aria-label="Fermer"
              data-testid="download-close"
            >
              <X size={18} />
            </button>

            {stage === "form" && (
              <form onSubmit={submitForm} className="p-8 md:p-12" data-testid="download-form">
                <div className="meta-label text-cinabre">{meta.eyebrow}</div>
                <h3 className="mt-3 font-serif text-3xl md:text-4xl leading-[1.05] tracking-tight">{meta.label}</h3>
                {identityToken ? (
                  <div className="mt-4 flex items-center gap-3 px-4 py-3 border border-cinabre/40 bg-cinabre/[0.06] text-[13px] text-fg/85" data-testid="dl-identified-badge">
                    <CheckCircle2 size={16} className="text-cinabre shrink-0" strokeWidth={1.6} />
                    <span>
                      <strong className="font-medium text-fg">Identifié :</strong> vous êtes déjà vérifié — pas besoin de recevoir un nouveau code.
                      <button type="button" onClick={() => { localStorage.removeItem("fdm_identity"); setIdentityToken(null); }} className="ml-2 underline decoration-cinabre underline-offset-2 hover:text-cinabre">Se déconnecter</button>
                    </span>
                  </div>
                ) : (
                  <p className="mt-4 text-fg/70 leading-relaxed max-w-lg text-[15px]">
                    Le téléchargement est soumis à l'identification préalable et à la vérification de votre adresse email par un code à 6 chiffres.
                  </p>
                )}

                <div className="mt-8 grid md:grid-cols-2 gap-6">
                  <label className="block">
                    <span className="meta-label text-fg/60">Nom complet *</span>
                    <input required value={form.name} onChange={update("name")}
                      className="mt-2 w-full border-b border-fg/30 bg-transparent py-3 font-serif text-xl focus:outline-none focus:border-cinabre"
                      data-testid="dl-name" />
                  </label>
                  <label className="block">
                    <span className="meta-label text-fg/60">Email *</span>
                    <input required type="email" value={form.email} onChange={update("email")}
                      className="mt-2 w-full border-b border-fg/30 bg-transparent py-3 font-serif text-xl focus:outline-none focus:border-cinabre"
                      data-testid="dl-email" />
                  </label>
                  <label className="block">
                    <span className="meta-label text-fg/60">Organisation · entreprise · fondation</span>
                    <input value={form.organization} onChange={update("organization")}
                      placeholder="Ou « Particulier »"
                      className="mt-2 w-full border-b border-fg/30 bg-transparent py-3 font-serif text-xl focus:outline-none focus:border-cinabre placeholder:text-fg/30"
                      data-testid="dl-organization" />
                  </label>
                  <label className="block">
                    <span className="meta-label text-fg/60">Fonction</span>
                    <input value={form.role} onChange={update("role")}
                      placeholder="Directeur mécénat, particulier…"
                      className="mt-2 w-full border-b border-fg/30 bg-transparent py-3 font-serif text-xl focus:outline-none focus:border-cinabre placeholder:text-fg/30"
                      data-testid="dl-role" />
                  </label>
                  <label className="block">
                    <span className="meta-label text-fg/60">Téléphone (optionnel)</span>
                    <input type="tel" value={form.phone} onChange={update("phone")}
                      className="mt-2 w-full border-b border-fg/30 bg-transparent py-3 font-serif text-xl focus:outline-none focus:border-cinabre"
                      data-testid="dl-phone" />
                  </label>
                  <label className="block md:col-span-2">
                    <span className="meta-label text-fg/60">Objet · à quoi ce document vous servira-t-il ?</span>
                    <textarea rows={3} value={form.purpose} onChange={update("purpose")}
                      placeholder="Diligence, étude en interne, dossier partenaire…"
                      className="mt-2 w-full border border-fg/20 bg-transparent p-4 font-serif text-lg leading-relaxed focus:outline-none focus:border-cinabre resize-vertical placeholder:text-fg/30"
                      data-testid="dl-purpose" />
                  </label>
                </div>

                {errMsg && (
                  <div className="mt-6 border border-cinabre/50 bg-cinabre/5 p-4 text-cinabre text-sm" data-testid="dl-error">
                    {errMsg}
                  </div>
                )}

                <div className="mt-10 flex items-center justify-between flex-wrap gap-4">
                  <p className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/50 max-w-md">
                    {identityToken ? "Aucun code ne sera renvoyé — accès direct." : "Nous vous enverrons un code à 6 chiffres pour confirmer votre email."}
                  </p>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="btn-editorial bg-fg text-bg border-fg hover:bg-cinabre hover:border-cinabre disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="dl-submit"
                  >
                    {submitting ? "…" : (identityToken ? "Accéder au document" : "Recevoir le code par email")} {identityToken ? <Download size={14} /> : <Mail size={14} />}
                  </button>
                </div>
              </form>
            )}

            {stage === "code" && (
              <form onSubmit={submitCode} className="p-8 md:p-12" data-testid="download-code-form">
                <div className="w-12 h-12 border border-cinabre text-cinabre flex items-center justify-center mb-6">
                  <Mail size={20} strokeWidth={1.4} />
                </div>
                <div className="meta-label text-cinabre">§ Étape 2 · Vérification</div>
                <h3 className="mt-3 font-serif text-3xl md:text-4xl leading-[1.05] tracking-tight">
                  Un code vous a été envoyé.
                </h3>
                <p className="mt-4 text-fg/70 leading-relaxed max-w-lg text-[15px]">
                  Vérifiez votre boîte email <strong className="text-fg font-medium">{form.email}</strong> (et vos spams). Saisissez le code à 6 chiffres ci-dessous. Il est valide 15 minutes.
                </p>

                <label className="block mt-8 max-w-sm">
                  <span className="meta-label text-fg/60">Code à 6 chiffres *</span>
                  <input
                    required
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    autoFocus
                    className="mt-2 w-full border-b border-fg/30 bg-transparent py-3 font-serif text-4xl tracking-[0.4em] focus:outline-none focus:border-cinabre"
                    placeholder="——————"
                    data-testid="dl-code"
                  />
                </label>

                {errMsg && (
                  <div className="mt-6 border border-cinabre/50 bg-cinabre/5 p-4 text-cinabre text-sm" data-testid="dl-code-error">
                    {errMsg}
                  </div>
                )}

                <div className="mt-10 flex items-center gap-3 flex-wrap">
                  <button
                    type="submit"
                    disabled={submitting || code.length < 6}
                    className="btn-editorial bg-fg text-bg border-fg hover:bg-cinabre hover:border-cinabre disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="dl-verify"
                  >
                    {submitting ? "Vérification…" : "Vérifier et télécharger"} <CheckCircle2 size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => { setStage("form"); setCode(""); setErrMsg(""); }}
                    className="btn-editorial"
                    data-testid="dl-back"
                  >
                    ← Modifier mes coordonnées
                  </button>
                </div>
              </form>
            )}

            {stage === "done" && (
              <div className="p-8 md:p-12" data-testid="download-success">
                <div className="w-12 h-12 border border-cinabre text-cinabre flex items-center justify-center">
                  <CheckCircle2 size={22} strokeWidth={1.4} />
                </div>
                <div className="mt-6 meta-label text-cinabre">§ Document prêt</div>
                <h3 className="mt-3 font-serif text-4xl leading-[1.05] tracking-tight">
                  Merci. Voici <em className="italic">{meta.label}</em>.
                </h3>
                <p className="mt-4 text-fg/70 leading-relaxed max-w-lg">
                  Votre email est vérifié. Vous pouvez ouvrir le document ci-dessous — il vous sera aussi accessible à tout moment via un nouveau code de vérification.
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <a
                    href={downloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-editorial bg-fg text-bg border-fg hover:bg-cinabre hover:border-cinabre"
                    data-testid="dl-open"
                  >
                    Ouvrir le PDF → <Download size={14} />
                  </a>
                  <button type="button" onClick={close} className="btn-editorial" data-testid="dl-done">
                    Fermer
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
